import { computed, ref, unref, type ComputedRef, type MaybeRef, type Ref } from 'vue'
import { useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'
import { storageSession } from '@/utils/storageService'
import { useSessionValues } from '@/composables/useSessionValues'
import { STUDIO_PLUS_PRODUCT_TYPE } from '@/constants/premiumProducts'

import apis from '@/services'
import type { SupportedCurrencyCode } from '@/constants/supportedCurrencies'
import type { PayPalPaymentSessionPayload, StudioPlusPriceResponse } from '@/services/premiumService/PremiumService'

type StudioPlusPriceQueryValue<T> = MaybeRef<T | null | undefined>

interface UseStudioPlusPriceParams {
  selectedAccommodationId: StudioPlusPriceQueryValue<number>
  currency?: StudioPlusPriceQueryValue<SupportedCurrencyCode | string>
  countryCode?: StudioPlusPriceQueryValue<string>
  postalCode?: StudioPlusPriceQueryValue<string>
  countryState?: StudioPlusPriceQueryValue<string>
  taxId?: StudioPlusPriceQueryValue<string>
  voucherCode?: StudioPlusPriceQueryValue<string>
  enabled?: MaybeRef<boolean>
}

interface UseStudioPlusPriceReturn {
  isLoading: Ref<boolean>
  isFetching: Ref<boolean>
  isSuccess: Ref<boolean>
  isError: Ref<boolean>
  studioPlusPrice: ComputedRef<StudioPlusPriceResponse | undefined>
}

const getAvailablePaymentMethods = async ({ currencyCode }) => {
  const response = await apis.premium.fetchAvailablePaymentMethods(currencyCode)
  return response?.data
}

export function useAvailablePaymentMethods(selectedAccommodationId, currencyCode) {
  const { isLoading, isFetching, isSuccess, isError, data, refetch } = useQuery({
    queryKey: ['availablePaymentMethods', selectedAccommodationId, currencyCode],
    queryFn: ({ queryKey }) => getAvailablePaymentMethods({ currencyCode: queryKey[2] }),
  })

  const availablePaymentMethodsData = computed(() => data?.value?.paymentMethods)
  const paypalConfig = computed(() => data?.value?.paypalConfig ?? null)

  return {
    isLoading,
    isFetching,
    isSuccess,
    isError,
    availablePaymentMethodsData,
    paypalConfig,
    refetch,
  }
}

export function useCreatePayPalPaymentSession() {
  const { mutateAsync: createPayPalPaymentSession } = useMutation({
    mutationFn: ({ amount, currency }: PayPalPaymentSessionPayload) =>
      apis.premium.createPayPalPaymentSession({ amount, currency }),
  })

  return { createPayPalPaymentSession }
}

const getExistingPaymentMethods = async () => {
  const response = await apis.premium.fetchExistingPaymentMethods()
  return response?.data?.paymentMethods
}

/**
 * Restrict query to billing-authorized users only
 * Backend endpoint allows admin role and limited admin-tool authority
 * Using 'enabled' prevents unnecessary failed requests
 */
export function useExistingPaymentMethods(selectedAccommodationId) {
  const { hasAdminOrSupport } = useSessionValues()
  const isQueryEnabled = computed(() => {
    const accommodationId = unref(selectedAccommodationId)
    return !!accommodationId && unref(hasAdminOrSupport)
  })
  const { isLoading, isFetching, isSuccess, isError, data } = useQuery({
    queryKey: ['existingPaymentMethods', selectedAccommodationId],
    enabled: isQueryEnabled,
    queryFn: () => getExistingPaymentMethods(),
  })

  const existingPaymentMethods = computed(() => data?.value)
  const defaultPaymentMethod = computed(() => existingPaymentMethods?.value?.find(method => method.isDefault))

  return {
    existingPaymentMethodsLoading: isLoading,
    isFetching,
    isSuccess,
    isError,
    existingPaymentMethods,
    defaultPaymentMethod,
  }
}

const getZuoraAccountData = async () => {
  return await apis.premium.fetchZuoraAccount()
}

/**
 * Restrict query to billing-authorized users only
 * Backend endpoint allows admin role and limited admin-tool authority
 * Using 'enabled' prevents unnecessary failed requests
 */
export function useZuoraAccount() {
  const { hasAdminOrSupport, token } = useSessionValues()
  const isQueryEnabled = computed(() => {
    return !!unref(token) && unref(hasAdminOrSupport)
  })
  const { isLoading, isFetching, isSuccess, isError, data, fetchStatus } = useQuery({
    queryKey: ['zuoraAccount', token],
    queryFn: () => getZuoraAccountData(),
    enabled: isQueryEnabled,
  })

  const isInitialLoading = computed(() => isLoading.value && fetchStatus.value !== 'idle')
  const zuoraAccountData = computed(() => data.value?.data)
  const hasNoBillingInfo = computed(() => data.value?.status === 204)

  return {
    isInitialLoading,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    hasNoBillingInfo,
    zuoraAccountData,
  }
}

export function useStudioPlusCampaign(selectedAccommodationId) {
  const { isLoading, isFetching, isSuccess, isError, data, fetchStatus } = useQuery({
    enabled: computed(() => !!selectedAccommodationId?.value),
    queryKey: ['studioPlusCampaign', selectedAccommodationId],
    queryFn: ({ queryKey }) => apis.premium.fetchStudioPlusCampaign({ accommodationId: queryKey[1] }),
  })

  const studioPlusCampaign = computed(() => (data?.value?.data ? data?.value?.data : null))

  const isExpiredSubscription = computed(() => {
    const expirationDate = studioPlusCampaign.value?.endDate
      ? studioPlusCampaign.value?.endDate.replace('@', 'T')
      : null

    return expirationDate ? new Date() > new Date(expirationDate) : false
  })

  const isCancelledSubscription = computed(() => {
    return studioPlusCampaign.value?.endDate !== null && !isExpiredSubscription.value
  })

  const isPro = computed(() => studioPlusCampaign.value?.type === STUDIO_PLUS_PRODUCT_TYPE.PRO)
  const isPlus = computed(() => studioPlusCampaign.value?.type === STUDIO_PLUS_PRODUCT_TYPE.PLUS)
  const isPlusDataInitialLoading = computed(() => isLoading.value && fetchStatus.value !== 'idle')

  return {
    isPlusDataInitialLoading,
    isLoading,
    isFetching,
    isSuccess,
    isError,
    studioPlusCampaign,
    isExpiredSubscription,
    isCancelledSubscription,
    isPro,
    isPlus,
  }
}

export function useStudioPlusPrice({
  selectedAccommodationId,
  currency,
  countryCode,
  postalCode,
  countryState,
  taxId,
  voucherCode,
  enabled = ref(true),
}: UseStudioPlusPriceParams): UseStudioPlusPriceReturn {
  const isQueryEnabled = computed(() => {
    const enabledValue = unref(enabled)
    const accommodationId = unref(selectedAccommodationId)

    return !!enabledValue && !!accommodationId
  })

  const { isLoading, isFetching, isSuccess, isError, data } = useQuery({
    queryKey: [
      'studioPlusPrice',
      selectedAccommodationId,
      countryCode,
      postalCode,
      countryState,
      taxId,
      currency,
      voucherCode,
    ],
    enabled: isQueryEnabled,
    queryFn: () => {
      const accommodationId = unref(selectedAccommodationId)
      if (!accommodationId) return undefined

      const keyCountryCode = unref(countryCode)

      return apis.premium.fetchStudioPlusPrice({
        accommodationId,
        countryCode: keyCountryCode ?? null,
        postalCode: unref(postalCode) ?? null,
        state: ['BR', 'US', 'CA', 'MX'].includes(keyCountryCode ?? '') ? (unref(countryState) ?? null) : null,
        vatId: unref(taxId) ?? null,
        currency: unref(currency) ?? 'EUR',
        voucherCode: unref(voucherCode) ?? '',
      })
    },
  })

  const studioPlusPrice = computed(() => data?.value?.data)

  return {
    isLoading,
    isFetching,
    isSuccess,
    isError,
    studioPlusPrice,
  }
}

export function usePreviewPrice({ selectedAccommodationId, productType, enabled = ref(true) }) {
  const isQueryEnabled = computed(() => {
    const enabledValue = unref(enabled)
    const accommodationId = unref(selectedAccommodationId)

    return !!enabledValue && !!accommodationId
  })

  const { isLoading, isFetching, isError, data } = useQuery({
    queryKey: ['previewPrice', selectedAccommodationId, productType],
    enabled: isQueryEnabled,
    queryFn: ({ queryKey }) =>
      apis.premium.fetchPreviewPrice({
        accommodationId: queryKey[1],
        productType: queryKey[2] ?? STUDIO_PLUS_PRODUCT_TYPE.PRO,
      }),
  })

  const previewPrice = computed(() => data?.value?.data)
  const amount = computed(() => previewPrice.value?.amount ?? null)
  const amountWithoutTax = computed(() => previewPrice.value?.amountWithoutTax ?? null)
  const nextRenewalDate = computed(() => previewPrice.value?.nextRenewalDate ?? null)
  const isActive = computed(() => previewPrice.value?.isActive ?? false)
  const isCancelled = computed(() => previewPrice.value?.isCancelled ?? false)

  return {
    isLoading,
    isFetching,
    isError,
    amount,
    amountWithoutTax,
    nextRenewalDate,
    isActive,
    isCancelled,
  }
}

export function useZuoraNewAccountMutation() {
  const queryClient = useQueryClient()
  const { userRole } = useSessionValues()
  const {
    isPending: isCreatingNewZuoraAccount,
    isSuccess: isCreatingNewZuoraAccountSuccess,
    isError: isCreatingNewZuoraAccountError,
    mutate: createNewZuoraAccountMutation,
  } = useMutation({
    mutationFn: ({ body, language }) => apis.premium.sendZuoraAccountCreation({ body, language }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zuoraAccount'] })
      queryClient.invalidateQueries({ queryKey: ['existingPaymentMethods'] })
    },
  })

  const createNewZuoraAccount = (...args) => {
    if (userRole.value !== 'admin') return

    createNewZuoraAccountMutation(...args)
  }

  return {
    isCreatingNewZuoraAccount,
    isCreatingNewZuoraAccountSuccess,
    isCreatingNewZuoraAccountError,
    createNewZuoraAccount,
  }
}

export function useZuoraUpdateAccountMutation() {
  const queryClient = useQueryClient()
  const { userRole } = useSessionValues()
  const {
    isPending: isUpdatingZuoraAccount,
    isSuccess: isUpdatingZuoraAccountSuccess,
    isError: isUpdatingZuoraAccountError,
    mutate: updateZuoraAccountMutation,
  } = useMutation({
    mutationFn: ({ body, language }) => apis.premium.sendZuoraAccountUpdate({ body, language }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['zuoraAccount'] })
      queryClient.invalidateQueries({ queryKey: ['existingPaymentMethods'] })
    },
  })

  const updateZuoraAccount = (...args) => {
    if (userRole.value !== 'admin') return

    updateZuoraAccountMutation(...args)
  }

  return {
    isUpdatingZuoraAccount,
    isUpdatingZuoraAccountSuccess,
    isUpdatingZuoraAccountError,
    updateZuoraAccount,
  }
}
export function useStudioPlusNewSubscriptionMutation() {
  const queryClient = useQueryClient()
  const { userRole } = useSessionValues()
  const {
    isPending: isSubscribingToStudioPlus,
    isSuccess: isSubscribingToStudioPlusSuccess,
    isError: isSubscribingToStudioPlusError,
    mutate: sendStudioPlusNewSubscriptionMutation,
  } = useMutation({
    mutationFn: ({
      accommodationId,
      partnerId,
      itemName,
      email,
      language,
      studioUserId,
      firstName,
      lastName,
      voucherCode,
      subscriptionSource,
    }) =>
      apis.premium.sendStudioPlusSubscription({
        accommodationId,
        body: {
          partnerId,
          itemName,
          email,
          language,
          studioUserId,
          firstName,
          lastName,
          voucherCode,
          subscriptionSource,
        },
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['Notifications'] })
      queryClient.invalidateQueries({ queryKey: ['studioPlusCampaign'] })
      queryClient.invalidateQueries({ queryKey: ['premiumProductsData'] })
    },
  })

  const sendStudioPlusNewSubscription = (...args) => {
    if (userRole.value !== 'admin') return

    sendStudioPlusNewSubscriptionMutation(...args)
  }

  return {
    isSubscribingToStudioPlus,
    isSubscribingToStudioPlusSuccess,
    isSubscribingToStudioPlusError,
    sendStudioPlusNewSubscription,
  }
}

export function useCancelStudioPlusMutation() {
  const { userRole } = useSessionValues()
  const {
    isPending: isCancelStudioPlusLoading,
    isSuccess: isCancelStudioPlusSuccess,
    isError: isCancelStudioPlusError,
    mutate: cancelStudioPlusMutation,
  } = useMutation({
    mutationFn: ({
      accommodationId,
      additionalInformation,
      itemName,
      email,
      reason,
      partnerId,
      language,
      studioUserId,
      firstName,
      lastName,
    }) =>
      apis.premium.cancelSubscription({
        accommodationId,
        body: {
          itemName,
          email,
          reason,
          partnerId,
          language,
          studioUserId,
          firstName,
          lastName,
          additionalInformation,
        },
      }),
  })

  const cancelStudioPlus = (...args) => {
    if (userRole.value !== 'admin') return

    cancelStudioPlusMutation(...args)
  }

  return {
    cancelStudioPlus,
    isCancelStudioPlusLoading,
    isCancelStudioPlusSuccess,
    isCancelStudioPlusError,
  }
}

export function useErrorMetrics() {
  const { userRole } = useSessionValues()
  const {
    isPending: sendErrorMetricsLoading,
    isSuccess: sendErrorMetricsSuccess,
    isError: sendErrorMetricsError,
    mutate: sendErrorMetricsMutation,
  } = useMutation({
    mutationFn: ({ message, code, productName, itemId }) =>
      apis.premium.sendErrorMetrics({
        code,
        message,
        productName,
        itemId,
      }),
  })

  const sendErrorMetrics = (...args) => {
    if (userRole.value !== 'admin') return

    sendErrorMetricsMutation(...args)
  }

  return {
    sendErrorMetricsLoading,
    sendErrorMetricsSuccess,
    sendErrorMetricsError,
    sendErrorMetrics,
  }
}

export function useValidateRCVoucher({ accommodationId, voucherCode, currencyCode }) {
  const isQueryEnabled = computed(() => !!unref(voucherCode) && !!unref(accommodationId))

  const { data, isFetching, isError } = useQuery({
    queryKey: ['rcVoucher', accommodationId, voucherCode, currencyCode],
    queryFn: ({ queryKey }) =>
      apis.rateConnect.validateRCVoucher({
        accommodationId: queryKey[1],
        voucherCode: queryKey[2] ?? '',
        currencyCode: queryKey[3],
      }),
    enabled: isQueryEnabled,
    staleTime: 0,
  })

  const rcVoucher = computed(() => data.value?.data)
  return { rcVoucher, isFetching, isError }
}

export function useCreateRateConnectCampaign() {
  const queryClient = useQueryClient()
  const { userRole } = useSessionValues()
  const {
    isPending: isCreatingNewRateConnectCampaign,
    isSuccess: isCreatingNewRateConnectCampaignSuccess,
    isError: isCreatingNewRateConnectCampaignError,
    mutateAsync: createNewRateConnectCampaignMutation,
  } = useMutation({
    mutationFn: ({ accommodationId, body }) => apis.rateConnect.createRateConnectCampaign({ accommodationId, body }),
    onSuccess: async (_, variables) => {
      storageSession.setItem('new_campaign_created', true)
      await queryClient.invalidateQueries({
        queryKey: ['RCCampaigns', variables.accommodationId],
      })
      await queryClient.refetchQueries({
        queryKey: ['RCCampaigns', variables.accommodationId],
      })
    },
  })

  const createNewRateConnectCampaign = async (...args) => {
    if (userRole.value !== 'admin') return

    return createNewRateConnectCampaignMutation(...args)
  }

  return {
    isCreatingNewRateConnectCampaign,
    isCreatingNewRateConnectCampaignSuccess,
    isCreatingNewRateConnectCampaignError,
    createNewRateConnectCampaign,
  }
}
